import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { CreditCard, Loader2, Send, ShieldCheck, X, MessageCircle, Lock, Key, Eye, Flag } from "lucide-react";
import { toast } from "sonner";

import { browseApi } from "@/api/browse";
import { messagesApi } from "@/api/messages";
import { recentlyViewedUtils } from "@/utils/recentlyViewed";
import { logger } from "@/utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Copied helper from App.js (behavior preserved)
const detectContactInfo = (text) => {
  if (!text) return false;
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phoneRegex = /[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}/;
  return emailRegex.test(text) || phoneRegex.test(text);
};

const PurchaseSection = ({ listing, user, login }) => {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const PLATFORM_FEE_PERCENT = 0.03;

  const platformFee = listing.price ? Math.round(listing.price * PLATFORM_FEE_PERCENT * 100) / 100 : 0;
  const totalAmount = listing.price ? listing.price + platformFee : 0;

  useEffect(() => {
    if (user && new URLSearchParams(location.search).get('contact') === '1') {
      setShowContactModal(true);
    }
  }, [user, location.search]);

  const breederConnectComplete = listing.breeder_connect_status === 'complete';
  const canPurchase = user && breederConnectComplete && listing.status === 'active' && !listing.is_locked;

  const handlePurchase = async () => {
    if (!canPurchase) return;

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/checkout/listing`,
        { listing_id: listing.listing_id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      logger.error("Checkout error:", error);
      toast.error(error.message || "Failed to initiate checkout");
      setCheckoutLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    if (detectContactInfo(message.trim())) {
      toast.warning("For your safety, avoid sharing phone numbers or emails in chat. Keep communication on Breedling.");
    }

    setSendingMessage(true);
    try {
      const data = await messagesApi.createInquiry({ listingId: listing.listing_id, message: message.trim() });
      toast.success("Message sent!");
      setShowContactModal(false);
      setMessage("");
      navigate(`/messages/${data.thread_id}`);
    } catch (error) {
      logger.error("Message error:", error);
      toast.error(error?.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="space-y-4">
      {listing.price && !listing.is_locked && (
        <div className="glass-card p-4 mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#A1A1AA]">Listing Price</span>
            <span className="text-[#EDEDED]">${listing.price.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#A1A1AA]">Platform Fee (3%)</span>
            <span className="text-[#EDEDED]">${platformFee.toLocaleString()}</span>
          </div>
          <div className="border-t border-white/10 pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-[#EDEDED] font-medium">Total</span>
              <span className="text-[#D4AF37] font-medium text-lg">${totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {!breederConnectComplete && listing.status === 'active' && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-4">
          <p className="text-sm text-yellow-400">⚠️ This breeder must complete payout setup before transactions can occur.</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        {user ? (
          <>
            <button
              onClick={handlePurchase}
              disabled={!canPurchase || checkoutLoading}
              className="btn-gold flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="purchase-btn"
            >
              {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {listing.status === 'sold' ? 'Sold' : 'Reserve / Purchase'}
            </button>
            <button
              onClick={() => setShowContactModal(true)}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="contact-breeder-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Breeder
            </button>
          </>
        ) : (
          <button
            onClick={() => login(`${location.pathname}?contact=1`)}
            className="btn-gold flex-1 flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            Sign in with Google to Contact Breeder
          </button>
        )}
      </div>

      <p className="text-xs text-[#71717A] text-center">A small platform service fee is added at checkout to cover secure payment processing and admin moderation.</p>

      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowContactModal(false)}>
          <div className="glass-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl text-[#EDEDED]">Contact Breeder</h3>
              <button onClick={() => setShowContactModal(false)} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-4 mb-4 p-3 bg-[#0A0A0A] rounded-lg">
              <img src={listing.photos?.[0] || "https://via.placeholder.com/80"} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <div>
                <p className="text-[#EDEDED]">{listing.title}</p>
                <p className="text-sm text-[#A1A1AA]">{listing.breeder_name}</p>
              </div>
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message to the breeder..."
              className="w-full h-32 bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-[#EDEDED] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-[#D4AF37]/50 resize-none"
              data-testid="contact-message-input"
            />

            <div className="flex gap-4 mt-4">
              <button onClick={() => setShowContactModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || sendingMessage}
                className="btn-gold flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-message-btn"
              >
                {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ListingDetail = ({ user, login }) => {
  const location = useLocation();
  const listingId = location.pathname.split('/').pop();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = useCallback(async () => {
    try {
      const id = window.location.pathname.split('/').pop();
      const data = await browseApi.listing(id);
      setListing(data.listing);
      
      // Check if user already has access
      if (data.listing && !data.listing.is_private_listing) {
        setHasAccess(true);
      } else if (data.listing && user && data.listing.approved_viewers?.includes(user.user_id)) {
        setHasAccess(true);
      }
      
      // Track recently viewed (only for public or unlocked)
      if (data.listing && !data.listing.is_private_listing) {
        recentlyViewedUtils.add(data.listing);
      }
    } catch (error) {
      logger.error("Failed to fetch listing:", error);
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  const handleSubmitReport = async () => {
    if (!reportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }
    
    setSubmittingReport(true);
    try {
      const response = await api.post(`/api/listings/${listing.listing_id}/report`, {
        reason: reportReason,
        details: reportDetails || null
      });
      toast.success(response.data.message || "Report submitted");
      setShowReportModal(false);
      setReportReason("");
      setReportDetails("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleUnlock = async () => {
    if (!inviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }
    
    setUnlocking(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      const res = await axios.post(
        `${API}/listings/${listingId}/access`,
        { listing_id: listingId, invite_code: inviteCode.trim().toUpperCase() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      if (res.data.access_granted) {
        toast.success("Access granted! Unlocking listing...");
        setHasAccess(true);
        // Refetch to get full listing data
        await fetchListing();
      }
    } catch (err) {
      logger.error("Failed to unlock:", err);
      toast.error(err.response?.data?.detail || "Invalid or expired invite code");
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#A1A1AA] text-lg mb-4">Listing not found</p>
          <Link to="/" className="btn-secondary">Back to Home</Link>
        </div>
      </div>
    );
  }

  // Private listing that user doesn't have access to
  const isLocked = listing.is_private_listing && !hasAccess;

  if (isLocked) {
    return (
      <div className="pt-24 pb-16 px-6 min-h-screen bg-[#050505]">
        <div className="max-w-xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Lock className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl text-[#EDEDED] mb-2">Private Listing</h1>
            <p className="text-[#A1A1AA] mb-6">
              This is an exclusive listing. Enter your invite code to view full details.
            </p>
            
            {/* Blurred Preview */}
            <div className="relative mb-6 rounded-xl overflow-hidden">
              <div className="aspect-video bg-[#18181B]">
                {listing.photos?.[0] && (
                  <img 
                    src={listing.photos[0]} 
                    alt="Preview" 
                    className="w-full h-full object-cover blur-xl opacity-50"
                  />
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <div className="text-center">
                  <Eye className="w-8 h-8 text-white/60 mx-auto mb-2" />
                  <p className="text-white/80 text-sm">Preview Hidden</p>
                </div>
              </div>
            </div>
            
            {/* Basic Info (what's visible) */}
            <div className="text-left mb-6 p-4 rounded-lg bg-[#18181B]">
              <p className="text-[#71717A] text-xs uppercase mb-1">Limited Info</p>
              <p className="text-[#EDEDED]">{listing.title}</p>
              <p className="text-[#A1A1AA] text-sm">{listing.species} • {listing.breed}</p>
            </div>
            
            {/* Invite Code Input */}
            <div className="space-y-3">
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="Enter invite code (e.g., ABC12345)"
                  className="input-minimal pl-10 text-center uppercase tracking-widest"
                  maxLength={8}
                  data-testid="invite-code-input"
                />
              </div>
              <button
                onClick={handleUnlock}
                disabled={unlocking || !inviteCode.trim()}
                className="w-full py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#B8962E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="unlock-btn"
              >
                {unlocking ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Unlock Listing
                  </>
                )}
              </button>
            </div>
            
            <p className="text-[#52525B] text-xs mt-4">
              Don't have a code? Contact the breeder directly to request access.
            </p>
          </div>
          
          <div className="text-center mt-6">
            <Link to="/" className="text-[#A1A1AA] hover:text-[#EDEDED] text-sm">
              ← Back to Browse
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        {/* Private Listing Badge */}
        {listing.is_private_listing && (
          <div className="mb-4 p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm">Private Listing - You have exclusive access</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-4 bg-[#18181B]">
              {listing.photos && listing.photos.length > 0 && listing.photos[0] ? (
                <img
                  src={listing.photos[0]}
                  alt={listing.title}
                  className={`w-full h-full object-cover ${listing.is_locked ? "blur-lg" : ""}`}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800&h=600&fit=crop";
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#18181B] to-[#27272A]">
                  <div className="text-center">
                    <svg className="w-24 h-24 mx-auto mb-4 text-[#3F3F46]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[#71717A] text-sm">No image available</p>
                  </div>
                </div>
              )}
            </div>
            {listing.photos?.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {listing.photos.slice(1, 5).map((photo, idx) => (
                  <img 
                    key={photo} 
                    src={photo} 
                    alt="" 
                    className="aspect-square rounded-lg object-cover"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
              {listing.species?.charAt(0).toUpperCase() + listing.species?.slice(1)} • {listing.breed}
            </p>
            <h1 className="text-3xl md:text-4xl text-[#EDEDED] mb-4">{listing.title}</h1>

            <div className="text-3xl text-[#D4AF37] font-medium mb-6">
              {listing.is_locked ? "Contact for Price" : `$${listing.price?.toLocaleString()}`}
            </div>

            <div className="glass-card p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {listing.age && (
                  <div>
                    <p className="text-[#A1A1AA]">Age</p>
                    <p className="text-[#EDEDED]">{listing.age}</p>
                  </div>
                )}
                {listing.sex && (
                  <div>
                    <p className="text-[#A1A1AA]">Sex</p>
                    <p className="text-[#EDEDED]">{listing.sex}</p>
                  </div>
                )}
                {listing.color && (
                  <div>
                    <p className="text-[#A1A1AA]">Color</p>
                    <p className="text-[#EDEDED]">{listing.color}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#A1A1AA]">Location</p>
                  <p className="text-[#EDEDED]">{listing.location_city}, {listing.location_state}</p>
                </div>
              </div>
            </div>

            {listing.registry_organization && (
              <div className="glass-card p-6 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                  <p className="text-[#EDEDED] font-medium">Registry Information</p>
                </div>
                <p className="text-[#A1A1AA] text-sm">
                  {listing.registry_organization} {listing.registration_number && `• #${listing.registration_number}`}
                </p>
              </div>
            )}

            {listing.verified_documents && (
              <div className="glass-card p-6 mb-6 border-[#22C55E]/30">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#22C55E]" />
                  <p className="text-[#22C55E] font-medium">Health Documents Verified</p>
                </div>
                <p className="text-[#A1A1AA] text-sm mt-1">
                  This listing's health documentation has been reviewed and verified by Breedling.
                </p>
              </div>
            )}

            {listing.description && (
              <div className="mb-6">
                <h3 className="text-lg text-[#EDEDED] mb-2">Description</h3>
                <p className="text-[#A1A1AA] leading-relaxed">{listing.description}</p>
              </div>
            )}

            {listing.shipping_available && (
              <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-6">
                <span className="text-[#D4AF37]">✓</span>
                <span>Shipping may be available</span>
                <span className="text-xs">(Breedling does not arrange transportation)</span>
              </div>
            )}

            <PurchaseSection listing={listing} user={user} login={login} />

            {listing.breeder_name && (
              <div className="mt-6 pt-6 border-t border-white/5">
                <p className="text-sm text-[#A1A1AA]">Listed by</p>
                <p className="text-[#EDEDED]">{listing.breeder_name}</p>
              </div>
            )}

            {/* Report Listing */}
            {user && listing.breeder_id !== user.user_id && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="text-xs text-[#71717A] hover:text-[#EF4444] flex items-center gap-1 transition-colors"
                  data-testid="report-listing-btn"
                >
                  <Flag className="w-3 h-3" />
                  Report this listing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowReportModal(false)}>
          <div className="glass-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl text-[#EDEDED]">Report Listing</h3>
              <button onClick={() => setShowReportModal(false)} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-[#A1A1AA] mb-4">
              Help keep Breedling safe by reporting content that violates our guidelines.
            </p>

            <div className="mb-4">
              <label className="block text-sm text-[#A1A1AA] mb-2">Reason *</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-[#EDEDED] focus:outline-none focus:border-[#D4AF37]/50"
                data-testid="report-reason-select"
              >
                <option value="">Select a reason</option>
                <option value="fraud">Fraud / Scam</option>
                <option value="misleading">Misleading Information</option>
                <option value="animal_welfare">Animal Welfare Concern</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="spam">Spam</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-[#A1A1AA] mb-2">Additional Details (optional)</label>
              <textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Provide any additional information..."
                className="w-full h-24 bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-[#EDEDED] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                data-testid="report-details-input"
              />
            </div>

            <div className="flex gap-4">
              <button onClick={() => setShowReportModal(false)} className="btn-secondary flex-1">
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={!reportReason || submittingReport}
                className="btn-gold flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-report-btn"
              >
                {submittingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingDetail;
